import { useStations } from '@app/core/stations/hooks/useStations';
import EmergencyIcon from '@mui/icons-material/Emergency';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { ToggleButtonGroup } from '@mui/material';
import { StatusFilterToggleButton } from './StatusFilterToggleButton';
import { useMemo } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { getStationAccessFilterType } from '../../helpers/get-station-access-filter-type';
import { useStatusQueryFilter } from '../../hooks/useStatusQueryFilter';
import { StationAccessFilterType, stationAccessMessages } from '../options';

const stationAccessGroupMessage = defineMessage({
  id: 'logger.statusScreen.stationAccess',
  defaultMessage: 'Acceso a emisoras',
});

const STATION_ACCESS_FILTERS = [
  {
    type: StationAccessFilterType.OWNED,
    icon: HomeWorkIcon,
    message: stationAccessMessages.owned,
  },
  {
    type: StationAccessFilterType.SHARED,
    icon: EmergencyIcon,
    message: stationAccessMessages.shared,
  },
] as const;

const ALL_STATION_ACCESS_FILTERS = STATION_ACCESS_FILTERS.map(({ type }) => type);

/**
 * Non-exclusive toggle group for owned vs shared stations.
 * No access param = show all (both options selected in UI).
 * Renders nothing when the user has only owned or only shared stations.
 */
export function StatusStationAccessFilter() {
  const { $t } = useIntl();
  const stations = useStations();
  const { queryParams, setQueryParams } = useStatusQueryFilter();

  const availableAccessTypes = useMemo((): StationAccessFilterType[] => {
    const types = new Set(stations.map(getStationAccessFilterType));

    return ALL_STATION_ACCESS_FILTERS.filter((type) => types.has(type));
  }, [stations]);

  const counts = useMemo(() => {
    const countsByAccess: Record<StationAccessFilterType, number> = {
      [StationAccessFilterType.OWNED]: 0,
      [StationAccessFilterType.SHARED]: 0,
    };

    for (const station of stations) {
      countsByAccess[getStationAccessFilterType(station)]++;
    }

    return countsByAccess;
  }, [stations]);

  const selectedAccessTypes = useMemo(() => {
    if (queryParams.access == null) {
      return availableAccessTypes;
    }

    const filteredSelection = queryParams.access.filter((type) =>
      availableAccessTypes.includes(type),
    );

    return filteredSelection.length > 0 ? filteredSelection : availableAccessTypes;
  }, [queryParams.access, availableAccessTypes]);

  if (availableAccessTypes.length <= 1) {
    return null;
  }

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: StationAccessFilterType[],
  ) => {
    if (newValue.length === 0 || newValue.length === availableAccessTypes.length) {
      setQueryParams({ access: null });
    } else {
      setQueryParams({ access: newValue });
    }
  };

  return (
    <ToggleButtonGroup
      value={selectedAccessTypes}
      onChange={handleChange}
      aria-label={$t(stationAccessGroupMessage)}
      size="small"
    >
      {STATION_ACCESS_FILTERS.filter(({ type }) => availableAccessTypes.includes(type)).map(
        ({ type, icon, message }) => (
          <StatusFilterToggleButton
            key={type}
            value={type}
            icon={icon}
            label={$t(message)}
            count={counts[type]}
            color="info"
          />
        ),
      )}
    </ToggleButtonGroup>
  );
}
